(ns lupapalvelu.ui2.dropdown
  (:require ["react" :as react]
            ["react-dom" :as react-dom]
            [lupapalvelu.ui2.button :refer [button]]
            [lupapalvelu.ui2.variant-builder :refer [builder merge-props-classes]]
            [reagent.core :as r :refer [defc]]
            [reagent.hooks :as hooks]))

(def classes
  (builder
    {:base     {:dropdown              ["fixed"
                                        "bg-background-container-primary" "text-text-lightbg"
                                        "rounded-md" "shadow-lg"
                                        "border" "border-navibar-menu-stroke"
                                        "min-w-[180px]"
                                        "list-none" "m-0" "p-1"
                                        "z-[2500]"]
                :separator             ["border-t" "border-navibar-menu-stroke-vertical-divider" "my-1"]
                :dropdown-header       ["px-4" "py-3"
                                        "border-b" "border-navibar-menu-stroke-vertical-divider"
                                        "mb-1"]
                :dropdown-header-title ["text-sm" "font-semibold" "text-text-lightbg"]
                :dropdown-header-sub   ["text-xs" "text-text-muted" "mt-0.5"]
                :menu-item             ["w-full" "justify-start"
                                        "font-medium"
                                        "gap-3"
                                        "text-left" "whitespace-nowrap"
                                        "border-0" "shadow-none"
                                        "bg-background-container-primary" "text-text-lightbg"
                                        "hover:bg-background-container-secondary"]
                :trigger               ["px-3" "py-2"
                                        "rounded-md"
                                        "text-sm" "font-medium"
                                        "cursor-pointer"
                                        "transition-all"
                                        "border-0" "shadow-none"]}
     :variants {:theme    {:light {:trigger ["text-text-lightbg"
                                             "hover:bg-background-container-secondary"]}
                           :dark  {:trigger ["text-white"
                                             "hover:bg-white/10"]}}
                :disabled {true  {:menu-item ["pointer-events-none" "opacity-40"]}
                           false {}}
                :active   {true  {:menu-item ["bg-background-container-secondary"]}
                           false {}}}}))

(defn- menu-style
  [trigger-el align]
  (when trigger-el
    (let [rect  (.getBoundingClientRect trigger-el)
          width (.-innerWidth js/window)]
      #js {:top      (+ (.-bottom rect) 4)
           :minWidth (.-width rect)
           :left     (when (= :left align)
                       (.-left rect))
           :right    (when (not= :left align)
                       (- width (.-right rect)))})))

(defn- focusable-menu-items
  [menu-el]
  (vec (when menu-el
         (array-seq (.querySelectorAll menu-el "[role='menuitem']:not([disabled]):not([aria-disabled='true'])")))))

(defn- focus-menu-item!
  [menu-el direction]
  (let [items       (focusable-menu-items menu-el)
        active-el   (.-activeElement js/document)
        current-idx (or (first (keep-indexed (fn [idx item]
                                               (when (= item active-el)
                                                 idx))
                                             items))
                        -1)]
    (when-let [item (get items (+ current-idx direction))]
      (.focus item)
      true)))

(defn- dropdown-item
  [{:keys [label icon href on-click disabled? active? separator-before? close-menu!]}]
  (let [link? (and href (not disabled?))]
    [:<>
     (when separator-before?
       [:li [:hr (merge-props-classes {} (classes {} :separator))]])
     [:li {:role "none"}
      [button (merge {:el           (if link? :a :button)
                      :role         "menuitem"
                      :intent       :text
                      :size         :xs
                      :disabled     disabled?
                      :aria-current (when active? "page")
                      :class        (classes {:disabled disabled?
                                              :active   active?}
                                             :menu-item)
                      :on-click     (fn [e]
                                      (when (or (not link?) on-click)
                                        (.preventDefault e))
                                      (when (and on-click (not disabled?))
                                        (on-click e))
                                      (close-menu!))
                      :on-key-down  (fn [e]
                                      (when (and link? (= " " (.-key e)))
                                        (.preventDefault e)
                                        (.click (.-currentTarget e))))}
                     (if link?
                       {:href href}
                       {:type "button"}))
       (when icon [icon {:size 16 :aria-hidden "true"}])
       [:span label]]]]))

(defc dropdown-menu
  "Accessible dropdown menu with click-outside-to-close behaviour.

  Props:
    :trigger-content – reagent hiccup rendered inside the trigger button
    :items           – seq of item maps (see ns docstring)
    :aria-label      – aria-label for the trigger button (required)
    :align           – :left | :right (default :right)
    :header          – {:title string :subtitle string} shown above items
    :theme           – :dark | :light (default :light)"
  [{:keys [trigger-content items aria-label align header theme]}]
  (let [[open? set-open!]        (react/useState false)
        opened-with-keyboard-ref (react/useRef false)
        close!                   (react/useCallback (fn []
                                                      (set! (.-current opened-with-keyboard-ref) false)
                                                      (set-open! false))
                                                    #js [set-open!])
        container-ref            (react/useRef nil)
        trigger-ref              (react/useRef nil)
        menu-ref                 (react/useRef nil)]
    (hooks/use-effect
      (fn []
        (when open?
          (let [click-handler (fn [e]
                                (let [target          (.-target e)
                                      inside-trigger? (and (.-current ^js container-ref)
                                                           (.contains (.-current ^js container-ref) target))
                                      inside-menu?    (and (.-current ^js menu-ref)
                                                           (.contains (.-current ^js menu-ref) target))]
                                  (when-not (or inside-trigger? inside-menu?)
                                    (close!))))
                key-handler   (fn [e]
                                (case (.-key e)
                                  "Escape" (do
                                             (.preventDefault e)
                                             (close!))
                                  "Tab"    (when-not (seq (focusable-menu-items (.-current ^js menu-ref)))
                                             (close!))
                                  nil))]
            (.addEventListener js/document "mousedown" click-handler true)
            (.addEventListener js/document "keydown" key-handler true)
            (fn []
              (.removeEventListener js/document "mousedown" click-handler true)
              (.removeEventListener js/document "keydown" key-handler true)))))
      #js [open? close!])
    (hooks/use-effect
      (fn []
        (when (and open? (.-current ^js opened-with-keyboard-ref) (.-current ^js menu-ref))
          (focus-menu-item! (.-current ^js menu-ref) 1)))
      #js [open?])
    [:div {:class "relative" :ref container-ref}
     [button {:type          "button"
              :ref           trigger-ref
              :intent        :text
              :size          :sm
              :aria-haspopup "true"
              :aria-expanded (str open?)
              :aria-label    aria-label
              :on-click      (fn [e]
                               (.stopPropagation e)
                               (if open?
                                 (close!)
                                 (do
                                   (set! (.-current opened-with-keyboard-ref) (zero? (.-detail e)))
                                   (set-open! true))))
              :on-key-down   (fn [e]
                               (when (and open?
                                          (= "Tab" (.-key e))
                                          (not (.-shiftKey e))
                                          (focus-menu-item! (.-current ^js menu-ref) 1))
                                 (.preventDefault e)))
              :class         (classes {:theme (or theme :light)} :trigger)}
      trigger-content]
     (when open?
       (.createPortal react-dom
                      (r/as-element
                        [:ul (merge-props-classes {:role        "menu"
                                                   :ref         menu-ref
                                                   :style       (menu-style (.-current ^js container-ref)
                                                                            (or align :right))
                                                   :on-key-down (fn [e]
                                                                  (when (= "Tab" (.-key e))
                                                                    (.preventDefault e)
                                                                    (let [direction (if (.-shiftKey e) -1 1)
                                                                          moved?    (focus-menu-item! (.-currentTarget e) direction)]
                                                                      (when-not moved?
                                                                        (close!)
                                                                        (.focus (.-current ^js trigger-ref))))))
                                                   :class       ["twp" "lp-theme" "nobase"]}
                                                  (classes {} :dropdown))
                         (when header
                           [:li {:role "presentation"}
                            [:div (merge-props-classes {} (classes {} :dropdown-header))
                             [:div (merge-props-classes {} (classes {} :dropdown-header-title)) (:title header)]
                             (when (:subtitle header)
                               [:div (merge-props-classes {} (classes {} :dropdown-header-sub)) (:subtitle header)])]])
                         (map (fn [item]
                                ^{:key (:key item)}
                                [dropdown-item (assoc item :close-menu! close!)])
                              items)])
                      js/document.body))]))
